import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

export default function PrivacyPolicyPage() {
  const lastUpdated = "April 15, 2026";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F9F7" }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to App
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={28} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Privacy Policy</h1>
        </div>
        <p className="text-sm text-stone-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="bg-white rounded-lg border border-stone-200 p-6 sm:p-8 space-y-6 text-sm text-stone-700 leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>1. Introduction</h2>
            <p>Dining Floor Studio ("we," "our," or "us") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our restaurant floor management application (the "Service").</p>
            <p className="mt-2">By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>2. Information We Collect</h2>
            <p className="font-medium text-stone-800 mt-3">Personal Information</p>
            <p>When you register for an account, we may collect:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Account role (Admin, Host, Server)</li>
              <li>Login credentials (password is encrypted and never stored in plain text)</li>
            </ul>

            <p className="font-medium text-stone-800 mt-3">Operational Data</p>
            <p>In the course of using the Service, the following data is collected to enable restaurant management features:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Reservation and walk-in information (guest names, party sizes, phone numbers, special requests)</li>
              <li>Member/guest profiles and visit history</li>
              <li>Table layouts, floor plans, and section assignments</li>
              <li>Server schedules and performance metrics</li>
              <li>Service chat messages between staff</li>
              <li>Service session data and analytics</li>
            </ul>

            <p className="font-medium text-stone-800 mt-3">Automatically Collected Information</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Session activity (login timestamps, active session heartbeats)</li>
              <li>Browser notification preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>To provide, operate, and maintain the Service</li>
              <li>To manage user accounts and authentication</li>
              <li>To enable real-time restaurant floor management features</li>
              <li>To generate analytics and performance reports for restaurant operations</li>
              <li>To send in-app and push notifications related to service operations</li>
              <li>To track member visit history and loyalty tiers</li>
              <li>To improve and optimize the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>4. Data Storage & Security</h2>
            <p>Your data is stored in a MongoDB database. We implement industry-standard security measures including:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Password hashing using bcrypt encryption</li>
              <li>JWT (JSON Web Token) authentication for secure API access</li>
              <li>HTTPS encryption for all data in transit</li>
              <li>Role-based access controls (Admins, Hosts, Servers see only what their role permits)</li>
            </ul>
            <p className="mt-2">If you choose to self-host the Service, all data resides entirely on your own infrastructure and never leaves your network.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>5. Data Sharing & Third Parties</h2>
            <p>We do <strong>not</strong> sell, trade, or rent your personal information to third parties.</p>
            <p className="mt-2">The Service does <strong>not</strong> integrate with any third-party analytics, advertising, or tracking services. No data is transmitted to external services such as Google Analytics, Facebook, or any ad network.</p>
            <p className="mt-2">We may disclose your information only if required by law, regulation, or legal process.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>6. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide the Service. Specifically:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Chat messages are reset daily</li>
              <li>Service session data is retained for historical analytics</li>
              <li>Member profiles and visit history are retained until manually deleted by an administrator</li>
              <li>Account data is retained until the account is deleted by an administrator</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data (CSV export is available for administrators)</li>
              <li>Withdraw consent for notifications at any time via browser settings</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <a href="mailto:jordanlthomas2002@gmail.com" className="underline" style={{ color: "#2F4F4F" }}>jordanlthomas2002@gmail.com</a>.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>8. Browser Notifications</h2>
            <p>The Service uses browser push notifications to alert staff of new reservations, walk-ins, table assignments, and chat messages. Notification permission is requested on login and can be revoked at any time through your browser settings. No notification data is sent to external push notification services.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>9. Children's Privacy</h2>
            <p>The Service is intended for use by restaurant staff aged 18 and older. We do not knowingly collect information from children under 18.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>11. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact:</p>
            <div className="mt-2 p-3 rounded-lg bg-stone-50 border border-stone-100">
              <p className="font-medium text-stone-800">Dining Floor Studio</p>
              <p>Email: <a href="mailto:jordanlthomas2002@gmail.com" className="underline" style={{ color: "#2F4F4F" }}>jordanlthomas2002@gmail.com</a></p>
              <p>Jurisdiction: State of Georgia, USA</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
